import {
  Button,
  Dialog,
  Icon,
  Typography,
} from "@inductiveautomation/ws-components";
import { omit } from "lodash";
import { useTranslation } from "react-i18next";

import { getStartDialogStyles } from "../_training.styles";
import { StartDialogAttributes, StartDialogProps } from "../Training.types";

import {
  unearnedCertificationActions,
  visitInductiveUniversityAction,
} from "../Training.actions";

const getAttributes = (type: keyof StartDialogAttributes) => {
  const attributes: StartDialogAttributes = {
    "start-credential": {
      translationPrefix: "get_credentialed",
      image: <Icon name="credential" style={{ color: "#F7901E" }} />,
      actions: (t, onAction) => [
        visitInductiveUniversityAction(t, () =>
          onAction("visit-inductive-university")
        ),
      ],
    },
    "start-core-certification": {
      translationPrefix: "get_core_certified",
      image: <Icon name="certification" style={{ color: "#003E69" }} />,
      actions: (t, onAction) =>
        unearnedCertificationActions(t, onAction, "CORE").flat(),
    },
    "start-gold-certification": {
      translationPrefix: "get_gold_certified",
      image: <Icon name="certification" style={{ color: "#E9AF23" }} />,
      actions: (t, onAction) =>
        unearnedCertificationActions(t, onAction, "GOLD").flat(),
    },
    "start-elective-courses": {
      translationPrefix: "get_started_with_elective_courses",
      image: <Icon name="course-elective" />,
      actions: (t, onAction) => [
        visitInductiveUniversityAction(t, () =>
          onAction("visit-inductive-university")
        ),
      ],
    },
    "start-employee-courses": {
      translationPrefix: "get_started_with_employee_courses",
      image: <Icon name="course-employee" />,
      actions: (t, onAction) => [
        visitInductiveUniversityAction(t, () =>
          onAction("visit-inductive-university")
        ),
      ],
    },
  };
  return attributes[type];
};

const StartDialog = ({ isOpen, type, onClose, onAction }: StartDialogProps) => {
  const { translationPrefix, image, actions: getActions } = getAttributes(type);
  const { t } = useTranslation("translation", {
    keyPrefix: `training.actions.${translationPrefix}.dialog`,
  });
  const { t: unprefixedT } = useTranslation();
  const { classes } = getStartDialogStyles();
  const actions = getActions(unprefixedT, onAction).map((action) =>
    omit(action, [
      "iconLeft",
      "iconRight",
      "iconLeftVariant",
      "iconRightVariant",
    ])
  );

  return (
    <Dialog
      isOpen={isOpen}
      testId={`${type}-dialog`}
      headline={t("heading")}
      onClose={onClose}
      showCloseButton
      collapseContentScrollGutter
      helper={{
        text: t("helper_text"),
      }}
      contentArea={
        <div className={classes.content}>
          {image}

          <Typography className={classes.description}>
            {t("description")}
          </Typography>

          <div className={classes.actionsContainer}>
            {actions.map(({ id, icon, ...rest }) => (
              <Button
                key={`training-start-${id}`}
                {...rest}
                buttonVariant="primary"
                endIcon={icon ? <Icon name={icon} /> : undefined}
                icon={undefined}
              />
            ))}
          </div>
        </div>
      }
    />
  );
};

export default StartDialog;
